import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  IconButton,
  Modal,
  Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMember } from '../contexts/MemberContext';
import { ReactComponent as CinchLogo } from '../cinch.svg';
import { formattedPhoneNumber } from '../shared/utils';
import MemberHeader from '../components/MemberHeader';
import WelcomeSection from '../components/WelcomeSection';
import AlertBanner from '../components/AlertBanner';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CloseIcon from '@mui/icons-material/Close';
import QRCode from 'react-qr-code';

/**
 * Account Opted Out Page
 * Shows when a member has opted out of SMS messages
 */
const AccountOptedOutPage = () => {
  const navigate = useNavigate();
  const { deviceInfo } = useAuth();
  const { member, orientation } = useMember();
  const [qrModalOpen, setQrModalOpen] = useState(false);

  useEffect(() => {
    if (!member.is_optout) {
      navigate('/member-dashboard');
    }
  }, [member.is_optout]);


  // Handle QR code modal
  const handleOpenQrModal = () => {
    setQrModalOpen(true);
  };

  const handleCloseQrModal = () => {
    setQrModalOpen(false);
  };

  useEffect(() => {
    if (orientation === -1) {
      // the device was flipped over so navigate to the cashier login page.
      navigate('/cashier-login');
    }
  }, [orientation]);


  return (
    <Container sx={{
      py: 4,
      height: '100%',
      width: '100% !important',
      maxWidth: '1024px !important',
      px: '0 !important',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      {/* Navigation header */}
      <MemberHeader />

      {/* Welcome section */}
      <WelcomeSection />

      {/* Opt-out notification section */}
      <AlertBanner
        title="Account Opted Out"
        message={`Your phone number has opted out from receiving SMS messages from us. To access exclusive coupons and redeem your points, send "START" TO ${deviceInfo?.phone_number ? formattedPhoneNumber(deviceInfo.phone_number) : ''}`}
      >
        <Button
          variant="contained"
          onClick={handleOpenQrModal}
          startIcon={<QrCode2Icon />}
          sx={{
            bgcolor: 'white',
            color: '#4b5563',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.9)'
            },
            fontWeight: 'bold',
            px: 3,
            py: 1.5
          }}
        >
          Show QR code to opt in
        </Button>
      </AlertBanner>

      {/* Actions section */}
      <Box mt={4} mb={4} display="flex" justifyContent="center" gap={2}>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          onClick={() => navigate('/member-confirmation')}
        >
          Continue to earn rewards
        </Button>
      </Box>

      {/* Footer */}
      <Box pb={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body3" color="text.secondary">
          powered by
        </Typography>
        <Box ml={1} display="flex" alignItems="center">
          <CinchLogo
            width={75}
            style={{ marginRight: '4px' }}
          />
        </Box>
      </Box>

      {/* QR Code Modal */}
      <Modal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Paper
          sx={{
            position: 'relative',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3
          }}
        >
          <IconButton
            onClick={handleCloseQrModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary'
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h5" component="h2" gutterBottom>
            Scan to Opt In
          </Typography>

          <Box sx={{ bgcolor: 'white', p: 3, borderRadius: 2 }}>
            <QRCode
              value={`smsto:${deviceInfo?.phone_number}:START`}
              size={300}
              level="H"
              fgColor="#000000"
              bgColor="#ffffff"
            />
          </Box>

          <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
            Scan this QR code with your phone to opt in and start receiving exclusive offers
          </Typography>
        </Paper>
      </Modal>
    </Container>
  );
};

export default AccountOptedOutPage;